import React from 'react';
import { Node, NodeViewProps, wrappingInputRule } from '@tiptap/core';
import { TiptapNodeType } from '../tiptapTypes';
import { ReactNodeViewRenderer } from '@tiptap/react';

import { convertEmojiStringToUnifiedCode } from '../../emoji/emojiUtil';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        callout: {
            toggleCallout: () => ReturnType;
            unsetCallout: () => ReturnType;
        };
    }
}

interface CalloutOptions {
    // To prevent /common from importing /client files, we need to inject the NodeView component
    NodeView?: React.FC<NodeViewProps>;
}

const DEFAULT_CALLOUT_ICON = '⚠️';
const DEFAULT_CALLOUT_ICON_CODE = '26A0-FE0F';

// Try to match the github markdown:
//  https://docs.github.com/en/get-started/writing-on-github/getting-started-with-writing-and-formatting-on-github/basic-writing-and-formatting-syntax#alerts
export const CALLOUT_ICON_TO_MARKDOWN_KEY_MAPPING = new Map<string, string>();
CALLOUT_ICON_TO_MARKDOWN_KEY_MAPPING.set('ℹ', 'NOTE');
CALLOUT_ICON_TO_MARKDOWN_KEY_MAPPING.set('💡', 'TIP');
CALLOUT_ICON_TO_MARKDOWN_KEY_MAPPING.set('❗', 'IMPORTANT');
CALLOUT_ICON_TO_MARKDOWN_KEY_MAPPING.set('⚠️', 'WARNING');
CALLOUT_ICON_TO_MARKDOWN_KEY_MAPPING.set('❌', 'CAUTION');

const CALLOUT_ICON_MAPPING = new Map<string, string>();
CALLOUT_ICON_MAPPING.set('info', 'ℹ');
CALLOUT_ICON_MAPPING.set('note', 'ℹ');
CALLOUT_ICON_MAPPING.set('important', '❗');
CALLOUT_ICON_MAPPING.set('tip', '💡');
CALLOUT_ICON_MAPPING.set('idea', '💡');
CALLOUT_ICON_MAPPING.set('question', '❓');
CALLOUT_ICON_MAPPING.set('success', '✅');
CALLOUT_ICON_MAPPING.set('warning', '⚠️');
CALLOUT_ICON_MAPPING.set('error', '❌');

const calloutIconKeys = [...CALLOUT_ICON_MAPPING.keys()];
// Match any of the callout icon names, or emojis (including compound emojis)
const INPUT_REGEX = new RegExp(
    `^!(${calloutIconKeys.join('|')}|(\\p{Emoji}(?:\\p{Emoji_Modifier}|\uFE0F|\u200D\\p{Emoji})*)?)\\s`,
    'iu',
);

const getInputMatchIcon = (iconText: string): string | null => {
    if (!iconText) return DEFAULT_CALLOUT_ICON;

    const icon = CALLOUT_ICON_MAPPING.get(iconText.toLowerCase());
    return icon || iconText;
};

export const Callout = Node.create<CalloutOptions>({
    name: 'callout',

    content: 'paragraph+',

    group: 'block',

    defining: true,

    addAttributes() {
        return {
            icon: { default: DEFAULT_CALLOUT_ICON },
            iconCode: { default: DEFAULT_CALLOUT_ICON_CODE },
        };
    },

    addCommands() {
        return {
            toggleCallout:
                () =>
                ({ chain }) => {
                    return chain().clearBlockNodes().setNode(TiptapNodeType.paragraph).toggleWrap(this.name).run();
                },
            unsetCallout:
                () =>
                ({ commands }) => {
                    return commands.lift(this.name);
                },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="callout"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            {
                class: 'node-callout',
                'data-type': 'callout',
                'data-icon': HTMLAttributes.icon,
                'data-icon-code': HTMLAttributes.iconCode || convertEmojiStringToUnifiedCode(HTMLAttributes.icon),
            },
            0,
        ];
    },

    addNodeView() {
        if (!this.options.NodeView) {
            throw new Error('Callout extension requires a NodeView component to be provided in options.');
        }

        return ReactNodeViewRenderer(this.options.NodeView);
    },

    addKeyboardShortcuts() {
        return {
            /* eslint-disable @typescript-eslint/naming-convention */
            // Keeping the original Tiptap shortcut, in case it's standard
            'Mod-Shift-6': () => this.editor.commands.toggleCallout(),
        };
    },

    addInputRules() {
        return [
            wrappingInputRule({
                find: (text: string) => {
                    const match = INPUT_REGEX.exec(text);

                    if (!match) return null;

                    const itemGroup = match[1];
                    const icon = getInputMatchIcon(itemGroup);

                    if (!icon) return null;

                    return {
                        ...match,
                        index: 0,
                        text,
                        data: { icon },
                    };
                },
                type: this.type,
                joinPredicate: () => false,
                getAttributes: (match) => {
                    if (!match || !match.data || !match.data.icon) return {};

                    return {
                        icon: match.data.icon,
                        iconCode: convertEmojiStringToUnifiedCode(match.data.icon),
                    };
                },
            }),
        ];
    },
});
